import React, { useEffect, useState } from 'react';
import { Mail, Trash2, CheckCircle, Clock, Archive } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

interface ContactMessage {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    website?: string;
    subject?: string;
    message: string;
    status: 'new' | 'read' | 'archived';
    createdAt: string;
}

const ContactSection: React.FC = () => {
    const [messages, setMessages] = useState<ContactMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'new' | 'read' | 'archived'>('all');

    const API_BASE_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/contact`);
            const data = await response.json();
            if (data.success) {
                setMessages(data.data);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id: string, status: 'new' | 'read' | 'archived') => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/contact/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status }),
            });

            if (response.ok) {
                setMessages(prev => prev.map(msg =>
                    msg._id === id ? { ...msg, status } : msg
                ));
                toast.success(`Message marked as ${status}`);
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update message status');
        }
    };

    const deleteMessage = async (id: string) => {
        const confirmed = await confirmToast('Are you sure you want to delete this message?', {
            confirmText: 'Delete',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/contact/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setMessages(prev => prev.filter(msg => msg._id !== id));
                toast.success('Message deleted');
            }
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Failed to delete message');
        }
    };

    const filteredMessages = messages.filter(msg => {
        if (filter === 'all') return true;
        return msg.status === filter;
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'new':
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" /> New</span>;
            case 'read':
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Read</span>;
            case 'archived':
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><Archive className="w-3 h-3 mr-1" /> Archived</span>;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
                    <p className="text-gray-600 mt-1">Manage inquiries from your website</p>
                </div>
                <div className="flex gap-2">
                    {(['all', 'new', 'read', 'archived'] as const).map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f
                                ? 'bg-primary text-white shadow-sm'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                                }`}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid gap-4">
                {filteredMessages.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                        <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-gray-900">No messages found</h3>
                        <p className="text-gray-500">
                            {filter === 'all' ? "You haven't received any messages yet." : `No ${filter} messages found.`}
                        </p>
                    </div>
                ) : (
                    filteredMessages.map((msg) => (
                        <div
                            key={msg._id}
                            className={`bg-white rounded-xl p-6 border transition-all ${msg.status === 'new' ? 'border-primary/30 shadow-sm ring-1 ring-primary/10' : 'border-gray-200'
                                }`}
                        >
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-lg font-semibold text-gray-900">{msg.name}</h3>
                                        {getStatusBadge(msg.status)}
                                        <span className="text-xs text-gray-500 ml-auto md:ml-0">
                                            {formatDate(msg.createdAt)}
                                        </span>
                                    </div>

                                    {msg.subject && (
                                        <div className="mb-2 text-sm font-medium text-gray-800">
                                            Subject: <span className="font-normal text-gray-600">{msg.subject}</span>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-4 mb-3 text-sm">
                                        <a href={`mailto:${msg.email}`} className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                                            <Mail className="w-3.5 h-3.5" /> {msg.email}
                                        </a>
                                        {msg.phone && (
                                            <span className="text-gray-600 inline-flex items-center gap-1">
                                                Phone: <span className="text-gray-800">{msg.phone}</span>
                                            </span>
                                        )}
                                        {msg.website && (
                                            <a href={msg.website.startsWith('http') ? msg.website : `https://${msg.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                                                Website: {msg.website}
                                            </a>
                                        )}
                                    </div>

                                    {msg.message && (
                                        <div className="bg-gray-50 rounded-lg p-4 text-gray-700 whitespace-pre-wrap text-sm leading-relaxed border border-gray-100">
                                            {msg.message}
                                        </div>
                                    )}
                                </div>

                                <div className="flex md:flex-col gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-4 mt-4 md:mt-0">
                                    {msg.status !== 'read' && (
                                        <button
                                            onClick={() => updateStatus(msg._id, 'read')}
                                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                                            title="Mark as Read"
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            <span className="md:hidden">Mark Read</span>
                                        </button>
                                    )}
                                    {msg.status !== 'archived' && (
                                        <button
                                            onClick={() => updateStatus(msg._id, 'archived')}
                                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                                            title="Archive"
                                        >
                                            <Archive className="w-4 h-4" />
                                            <span className="md:hidden">Archive</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteMessage(msg._id)}
                                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        <span className="md:hidden">Delete</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ContactSection;
